// Creator Marketplace (Meta) discovery source for the PlannerV2 Discover screen.
//
// The Instagram Creator Marketplace Discovery API needs a Page access token that MUST stay
// server-side, so this talks to our FastAPI backend proxy (never Meta directly). Until that
// endpoint ships it falls back to clearly-flagged SAMPLE data so the flow is demoable. Fields are
// kept aligned to Meta's response so the dedicated CreatorMarketplaceList can surface all of them.

import { devConfig } from '../config';

// Base for the Creator Marketplace backend. Defaults to the app's API host in production; set
// REACT_APP_CM_API_BASE=/local-meta to route through the CRA dev proxy to the local FastAPI backend.
const CM_API_BASE = process.env.REACT_APP_CM_API_BASE || devConfig.API_HOST;
const CREATOR_MARKETPLACE_URL = `${CM_API_BASE}/api/v1/discover/creator-marketplace`;
const CM_CONNECT_URL = `${CM_API_BASE}/api/v1/brand/facebook/connect`;

// After a Facebook connect/login, hand the FB auth response to the backend so it can exchange the
// user token for the brand's Page token and store it for discovery calls. Fire-and-forget.
export async function connectCreatorMarketplaceBackend(authResponse) {
  try {
    const res = await fetch(CM_CONNECT_URL, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        accessToken: authResponse && authResponse.accessToken,
        userID: authResponse && authResponse.userID,
      }),
    });
    return res.ok ? await res.json() : null;
  } catch (err) {
    console.warn('[creator-marketplace] backend connect failed:', err.message);
    return null;
  }
}

// --- shape adapters: isolate Meta's real response shape HERE so the UI never changes ---

// Insight metrics may arrive as a scalar, or as an object { value } / { total_value: { value } }.
function scalar(v) {
  if (v && typeof v === 'object') return v.value ?? v.total_value?.value ?? null;
  return v ?? null;
}

// Meta returns audience demographics as GRANULARITY BREAKDOWNS of `creator_engaged_accounts`
// (dimensions: age, gender, top_countries, top_cities) and `creator_reach` (follow_type, media_type),
// NOT as a standalone `audience` object. This adapter normalises either the real nested breakdowns
// OR the flat sample object into { age, gender, top_countries, top_cities } as [{ label, pct }].
// TODO(real-payload): map Meta's exact breakdown JSON (e.g. total_value.breakdowns[].results[])
// once a live response is available — only THIS function changes, not the UI.
function parseAudience(creator) {
  const flat = creator.audience || {};
  const engaged = creator.creator_engaged_accounts;
  const breakdown = (metric, dimension) => {
    if (metric && typeof metric === 'object' && metric.breakdowns && metric.breakdowns[dimension]) {
      return metric.breakdowns[dimension].map((r) => ({
        label: r.label ?? (r.dimension_values && r.dimension_values[0]),
        pct: r.pct ?? r.value,
      }));
    }
    return null;
  };
  return {
    age: breakdown(engaged, 'age') || flat.age || [],
    gender: breakdown(engaged, 'gender') || flat.gender || [],
    top_countries:
      breakdown(engaged, 'top_countries') || breakdown(engaged, 'country') || flat.top_countries || [],
    top_cities: breakdown(engaged, 'top_cities') || breakdown(engaged, 'city') || flat.top_cities || [],
  };
}

// Normalise a single Meta creator into the shape CreatorMarketplaceList reads. Field names mirror
// the Meta Creator Marketplace API; missing values get safe defaults so the UI never crashes.
export function mapCreator(creator, index = 0) {
  const username = creator.username || `creator_${index}`;
  const asArray = (v) => (Array.isArray(v) ? v : []);
  return {
    id: creator.id || `cm_${username}_${index}`,
    username,
    name: creator.name || username,
    is_account_verified: Boolean(creator.is_account_verified),
    biography: creator.biography || '',
    country: creator.country || '',
    gender: creator.gender || '',
    age_bucket: creator.age_bucket || '',
    profile_picture_url: creator.profile_picture_url || '',
    portfolio_url: creator.portfolio_url || '',
    email: creator.email || '',
    onboarded_status: creator.onboarded_status || '',
    badges: asArray(creator.badges),
    // NOTE(verify): `interests` mirrors the discovery FILTER param `creator_interests`; confirm it
    // exists as a per-creator RESPONSE field against a live payload before relying on it. Rendered
    // defensively (empty array hides the chips) so it can't break the UI if absent.
    interests: asArray(creator.interests),
    instagram_url: `https://www.instagram.com/${username}`,
    // insights — scalar() tolerates either a plain number or Meta's { value } / { total_value } shape
    total_followers: scalar(creator.total_followers),
    creator_engaged_accounts: scalar(creator.creator_engaged_accounts),
    creator_reach: scalar(creator.creator_reach),
    reels_interaction_rate: scalar(creator.reels_interaction_rate),
    reels_hook_rate: scalar(creator.reels_hook_rate),
    // partnership signals
    has_brand_partnership_experience: Boolean(creator.has_brand_partnership_experience),
    past_brand_partnership_partners: asArray(creator.past_brand_partnership_partners),
    // drill-down data (shown in the detail dialog)
    audience: parseAudience(creator),
    recent_media: asArray(creator.recent_media),
    branded_content_media: asArray(creator.branded_content_media),
    past_partnership_ads_media: asArray(creator.past_partnership_ads_media),
    __source: 'creator_marketplace',
  };
}

// Deterministically derive audience + per-media data for a sample creator (no randomness).
function withSampleDetail(c) {
  const followers = c.total_followers || 50000;
  const gender =
    c.gender === 'male'
      ? [{ label: 'Male', pct: 58 }, { label: 'Female', pct: 40 }, { label: 'Other', pct: 2 }]
      : [{ label: 'Female', pct: 62 }, { label: 'Male', pct: 36 }, { label: 'Other', pct: 2 }];
  return {
    ...c,
    audience: {
      age: [
        { label: '13-17', pct: 6 },
        { label: '18-24', pct: 38 },
        { label: '25-34', pct: 34 },
        { label: '35-44', pct: 14 },
        { label: '45+', pct: 8 },
      ],
      gender,
      top_countries: [
        { label: 'India', pct: 82 },
        { label: 'USA', pct: 6 },
        { label: 'UAE', pct: 4 },
      ],
      top_cities: [
        { label: 'Mumbai', pct: 18 },
        { label: 'Delhi', pct: 15 },
        { label: 'Bengaluru', pct: 11 },
      ],
    },
    onboarded_status: 'onboarded',
    email: `${c.username.replace(/\./g, '')}@creators.example`,
    recent_media: [0, 1, 2, 3].map((m) => ({
      id: `${c.username}_m${m}`,
      product_type: m % 2 ? 'REELS' : 'FEED',
      media_type: m % 2 ? 'VIDEO' : 'IMAGE',
      permalink: `https://www.instagram.com/${c.username}`,
      thumbnail_url: '',
      creation_time: `2026-0${(m % 8) + 1}-15T10:00:00+0000`,
      caption: 'Sample post — real captions & thumbnails arrive with advanced access.',
      tagged_brand: null,
      likes: Math.round(followers * (0.03 + 0.008 * m)),
      comments: Math.round(followers * 0.0018 * (m + 1)),
      views: m % 2 ? Math.round(followers * (2 + m)) : null,
      shares: Math.round(followers * 0.0006 * (m + 1)),
    })),
    branded_content_media: (c.past_brand_partnership_partners || []).slice(0, 2).map((brand, m) => ({
      id: `${c.username}_bc${m}`,
      product_type: 'FEED',
      media_type: 'IMAGE',
      permalink: `https://www.instagram.com/${c.username}`,
      thumbnail_url: '',
      creation_time: `2026-0${(m % 8) + 1}-10T10:00:00+0000`,
      caption: `Paid partnership with ${brand}`,
      tagged_brand: brand,
      likes: Math.round(followers * (0.04 + 0.005 * m)),
      comments: Math.round(followers * 0.002 * (m + 1)),
      views: null,
      shares: Math.round(followers * 0.0008 * (m + 1)),
    })),
    past_partnership_ads_media: c.has_brand_partnership_experience
      ? [0, 1].map((m) => ({
          id: `${c.username}_ad${m}`,
          product_type: 'REELS',
          media_type: 'VIDEO',
          permalink: `https://www.instagram.com/${c.username}`,
          thumbnail_url: '',
          creation_time: `2026-0${(m % 8) + 1}-05T10:00:00+0000`,
          caption: 'Partnership ad creative',
          tagged_brand: (c.past_brand_partnership_partners || [])[m] || null,
          likes: Math.round(followers * (0.05 + 0.004 * m)),
          comments: Math.round(followers * 0.0025 * (m + 1)),
          views: Math.round(followers * (3 + m)),
          shares: Math.round(followers * 0.001 * (m + 1)),
        }))
      : [],
  };
}

function normaliseResponse(data) {
  const rows = Array.isArray(data.results) ? data.results : Array.isArray(data.data) ? data.data : [];
  return {
    results: rows.map(mapCreator),
    count: typeof data.count === 'number' ? data.count : rows.length,
    __sample: false,
  };
}

// Clearly-labeled SAMPLE creators (India) — preview only, never real Meta data. Full field set so
// the UI shows exactly what real advanced-access data will populate.
const SAMPLE_CREATORS = [
  {
    username: 'priya.beauty', name: 'Priya Sharma', is_account_verified: true, country: 'IN',
    gender: 'female', age_bucket: '25-34', interests: ['Beauty', 'Skincare'],
    biography: 'Delhi beauty & skincare creator · honest reviews, GRWM, drugstore finds',
    total_followers: 184000, creator_engaged_accounts: 42000, creator_reach: 610000,
    reels_interaction_rate: 7.8, reels_hook_rate: 31.4, has_brand_partnership_experience: true,
    past_brand_partnership_partners: ['Nykaa', "L'Oréal", 'Sugar'], badges: ['Top creator'],
    portfolio_url: 'https://priyasharma.media',
  },
  {
    username: 'fitwithaakash', name: 'Aakash Verma', is_account_verified: false, country: 'IN',
    gender: 'male', age_bucket: '25-34', interests: ['Fitness'],
    biography: 'Mumbai fitness coach · home workouts, nutrition, transformations',
    total_followers: 96000, creator_engaged_accounts: 21000, creator_reach: 340000,
    reels_interaction_rate: 6.1, reels_hook_rate: 27.9, has_brand_partnership_experience: true,
    past_brand_partnership_partners: ['HealthKart'], badges: [],
  },
  {
    username: 'the.desi.foodie', name: 'Desi Foodie', is_account_verified: true, country: 'IN',
    gender: 'female', age_bucket: '18-24', interests: ['Food'],
    biography: 'Street food & home recipes across India · Bengaluru',
    total_followers: 253000, creator_engaged_accounts: 58000, creator_reach: 890000,
    reels_interaction_rate: 9.2, reels_hook_rate: 38.6, has_brand_partnership_experience: true,
    past_brand_partnership_partners: ['Zomato', 'Swiggy'], badges: ['Rising'],
  },
  {
    username: 'wanderlust.raj', name: 'Raj Malhotra', is_account_verified: false, country: 'IN',
    gender: 'male', age_bucket: '25-34', interests: ['Travel'],
    biography: 'Budget travel across the Himalayas & beyond',
    total_followers: 71000, creator_engaged_accounts: 15000, creator_reach: 210000,
    reels_interaction_rate: 5.4, reels_hook_rate: 24.1, has_brand_partnership_experience: false,
    past_brand_partnership_partners: [], badges: [],
  },
  {
    username: 'techbytes.in', name: 'TechBytes India', is_account_verified: true, country: 'IN',
    gender: 'male', age_bucket: '25-34', interests: ['Technology'],
    biography: 'Gadget reviews, deals & explainers · Pune',
    total_followers: 128000, creator_engaged_accounts: 24000, creator_reach: 410000,
    reels_interaction_rate: 4.9, reels_hook_rate: 22.3, has_brand_partnership_experience: true,
    past_brand_partnership_partners: ['boAt', 'Realme'], badges: [],
  },
  {
    username: 'styled.by.meera', name: 'Meera Nair', is_account_verified: true, country: 'IN',
    gender: 'female', age_bucket: '25-34', interests: ['Fashion', 'Lifestyle'],
    biography: 'Everyday fashion & styling · Chennai',
    total_followers: 205000, creator_engaged_accounts: 47000, creator_reach: 720000,
    reels_interaction_rate: 8.3, reels_hook_rate: 34.0, has_brand_partnership_experience: true,
    past_brand_partnership_partners: ['Myntra', 'AJIO'], badges: ['Top creator'],
  },
];

function sampleResponse() {
  const results = SAMPLE_CREATORS.map((c, i) => mapCreator(withSampleDetail(c), i));
  return { results, count: results.length, __sample: true };
}

// Fetch Creator Marketplace creators via the backend proxy; fall back to sample data on any failure.
export async function getCreatorMarketplaceInfluencers(page = 1, formData = {}) {
  try {
    const res = await fetch(`${CREATOR_MARKETPLACE_URL}?page=${page}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ payload: formData }),
    });
    if (!res.ok) throw new Error(`creator-marketplace endpoint ${res.status}`);
    return normaliseResponse(await res.json());
  } catch (err) {
    console.warn('[creator-marketplace] using sample data:', err.message);
    return sampleResponse();
  }
}
